class CartRemoveButton extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('click', (event) => {
      event.preventDefault();
      const cartItems = this.closest('cart-items') || this.closest('cart-drawer-items');
      cartItems.updateQuantity(this.dataset.index, 0, null, null);
    });
  }
}

customElements.define('cart-remove-button', CartRemoveButton);

class CartItems extends HTMLElement {
  constructor() {
    super();
    this.lineItemStatusElement = document.getElementById('shopping-cart-line-item-status') || document.getElementById('CartDrawer-LineItemStatus');

    const debouncedOnChange = debounce((event) => this.onChange(event), ON_CHANGE_DEBOUNCE_TIMER);
    this.addEventListener('change', debouncedOnChange.bind(this));
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'cart-items') return;
      this.onCartUpdate();
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) this.cartUpdateUnsubscriber();
  }

  onChange(event) {
    this.updateQuantity(event.target.dataset.index, event.target.value, document.activeElement.getAttribute('name'), event.target.dataset.quantityVariantId);
  }

  onCartUpdate() {
    const sectionId = this.tagName === 'CART-DRAWER-ITEMS' ? 'cart-drawer' : 'main-cart-items';
    fetch(`${routes.cart_url}?section_id=${sectionId}`)
      .then(response => response.text())
      .then(responseText => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const selectors = this.tagName === 'CART-DRAWER-ITEMS'
          ? ['cart-drawer-items', '.cart-drawer__footer']
          : ['cart-items'];
        selectors.forEach(selector => {
          const target = document.querySelector(selector);
          const source = html.querySelector(selector);
          if (target && source) target.replaceWith(source);
        });
      }).catch(console.error);
  }

  getSectionsToRender() {
    return [
      {
        id: 'main-cart-items',
        section: document.getElementById('main-cart-items')?.dataset.id,
        selector: '.js-contents'
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      },
      {
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section'
      },
      {
        id: 'main-cart-footer',
        section: document.getElementById('main-cart-footer')?.dataset.id,
        selector: '.js-contents'
      }
    ];
  }

  updateQuantity(line, quantity, name, variantId) {
    this.enableLoading(line);

    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map(s => s.section),
      sections_url: window.location.pathname
    });

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), body })
      .then(response => response.text())
      .then(text => {
        const parsedState = JSON.parse(text);
        const cartDrawerElem = document.querySelector('cart-drawer') || document.querySelector('cart-items');
        const thresholdPrice = parseInt(cartDrawerElem.getAttribute('data-threshhold-price'), 10);

        // ✅ Immediately update the UI
        this.refreshSections(parsedState);
        this.updateLiveRegions(line, '');
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: parsedState, variantId });

        // ✅ Run free product logic in the background with fresh cart data
        setTimeout(() => {
          this.checkAndAddFreeProduct(parsedState, thresholdPrice);
          this.checkAndRemoveFreeProduct(parsedState, thresholdPrice);
        }, 0);
      })
      .catch(() => {
        this.querySelectorAll('.loading__spinner').forEach(el => el.classList.add('hidden'));
        const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
        errors.textContent = window.cartStrings.error;
      })
      .finally(() => this.disableLoading(line));
  }

  /** ✅ UPDATED METHOD: Uses fresh cart state */
  checkAndAddFreeProduct(parsedState, threshold) {
    fetch(`${routes.cart_url}.js`)
      .then(res => res.json())
      .then(latestCart => {
        const freeProductAlreadyInCart = latestCart.items.some(
          item => item.properties && item.properties._free_gift === 'true'
        );
        if (freeProductAlreadyInCart) return;

        return Promise.all(latestCart.items.map(item =>
          fetch(`${window.Shopify.routes.root}products/${item.handle}.js`)
            .then(res => res.json())
            .then(product => ({
              qualifies: product.tags.includes('location_xape'),
              price: item.final_line_price
            }))
            .catch(() => ({ qualifies: false, price: 0 }))
        )).then(results => {
          const qualifyingTotal = results.reduce((sum, { qualifies, price }) => qualifies ? sum + price : sum, 0);
          if (qualifyingTotal >= threshold) {
            const freeProductForm = document.getElementById('freeProduct__form');
            if (!freeProductForm) {
              console.warn('⚠️ Free product form not found in DOM.');
              return;
            }
            this.insertFreeProduct(freeProductForm);
          }
        });
      });
  }

  checkAndRemoveFreeProduct(parsedState, threshold) {
    fetch(`${routes.cart_url}.js`)
      .then(res => res.json())
      .then(latestCart => {
        Promise.all(latestCart.items.map(item =>
          fetch(`${window.Shopify.routes.root}products/${item.handle}.js`)
            .then(res => res.json())
            .then(product => ({ item, qualifies: product.tags.includes('location_xape') }))
            .catch(() => ({ item, qualifies: false }))
        )).then(results => {
          const qualifyingTotal = results.reduce((total, { item, qualifies }) =>
            qualifies ? total + item.final_line_price : total, 0);

          if (qualifyingTotal < threshold) {
            const freeProductLine = latestCart.items.find(
              item => item.properties && item.properties._free_gift === 'true'
            );

            if (freeProductLine) {
              const body = JSON.stringify({ id: freeProductLine.key, quantity: 0, sections: this.getSectionsToRender().map(s => s.section), sections_url: window.location.pathname });
              fetch(`${routes.cart_change_url}`, { ...fetchConfig(), body })
                .then((response) => {
                  return response.text();
                })
                .then((state) => {
                  const parsedState = JSON.parse(state);
                  console.log('✅ Free product removed');
                  this.onCartUpdate();

                  // ✅ Immediately update the UI
                  this.refreshSections(parsedState);
                });
            }
          }
        });
      })
      .catch(error => console.error('❌ Error removing free product:', error));
  }

  refreshSections(parsedState) {
    this.classList.toggle('is-empty', parsedState.item_count === 0);
    const cartDrawerWrapper = document.querySelector('cart-drawer');
    const cartFooter = document.getElementById('main-cart-footer');

    if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
    if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);

    this.getSectionsToRender().forEach(section => {
      const target = document.getElementById(section.id)?.querySelector(section.selector) || document.getElementById(section.id);
      const html = this.getSectionInnerHTML(parsedState.sections[section.section], section.selector);
      if (target && html) target.innerHTML = html;
    });
  }

  updateLiveRegions(line, message) {
    const errorElement = document.getElementById(`Line-item-error-${line}`) || document.getElementById(`CartDrawer-LineItemError-${line}`);
    if (errorElement) errorElement.querySelector('.cart-item__error-text').innerHTML = message;
    this.lineItemStatusElement.setAttribute('aria-hidden', true);

    const status = document.getElementById('cart-live-region-text') || document.getElementById('CartDrawer-LiveRegionText');
    status.setAttribute('aria-hidden', false);
    setTimeout(() => status.setAttribute('aria-hidden', true), 1000);
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector)?.innerHTML;
  }

  enableLoading(line) {
    const container = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    container?.classList.add('cart__items--disabled');
    [...this.querySelectorAll(`#CartItem-${line} .loading__spinner`), ...this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`)].forEach(el => el.classList.remove('hidden'));
    document.activeElement?.blur();
    this.lineItemStatusElement?.setAttribute('aria-hidden', false);
  }

  disableLoading(line) {
    if (window.location.href.includes("cart")) {
      if (document.body.classList.contains('free-product-disabled')) {
        window.location.reload();
      }
    }
    const container = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    container?.classList.remove('cart__items--disabled');
    [...this.querySelectorAll(`#CartItem-${line} .loading__spinner`), ...this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`)].forEach(el => el.classList.add('hidden'));
  }

  insertFreeProduct(form) {
    const config = fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];
    const formData = new FormData(form);
    formData.append('sections', this.getSectionsToRender().map(s => s.section));
    formData.append('sections_url', window.location.pathname);
    config.body = formData;

    fetch(`${routes.cart_add_url}`, config)
      .then(res => res.json())
      .then(data => {
        console.log('✅ Free product added');
        this.onCartUpdate();
      });
  }
}

customElements.define('cart-items', CartItems);


if (!customElements.get('cart-note')) {
  customElements.define(
    'cart-note',
    class CartNote extends HTMLElement {
      constructor() {
        super();
        this.addEventListener(
          'change',
          debounce((event) => {
            const body = JSON.stringify({ note: event.target.value });
            fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } });
          }, ON_CHANGE_DEBOUNCE_TIMER)
        );
      }
    }
  );
}